import React, { useContext, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  getSchedules,
  clearSuppressionState,
} from "../../../store/suppression/suppressionSlice";
import {
  CloudProviderContext,
  getAccountCloudType,
  shouldShowAzurePlaceholderView,
} from "../../../CloudProviderContext";
import SuppressionFilters from "./components/SuppressionFilters";
import { formatScheduleName, getSuppressionAccountLabels } from "./utils/scheduleNameFormatter";
import { ACCOUNT_DETAIL_FONT_SX } from "../../../components/AccountDetailPolicy/accountDetailTypography";
import {
  AccountDetailTooltip,
  TruncatedTooltipText,
} from "../../../components/AccountDetailPolicy/editAccountTooltipStyles";

const CELL_TEXT = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};

const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748b",
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  whiteSpace: "nowrap",
};

const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
};

const SEARCH_FIELD_SX = {
  flex: { xs: "1 1 160px", sm: "0 0 auto" },
  width: { xs: "auto", sm: 220 },
  minWidth: { xs: 0, sm: 220 },
  "& .MuiOutlinedInput-root": {
    height: 40,
    fontSize: "0.875rem",
    backgroundColor: "white",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    py: 0,
    height: "100%",
    boxSizing: "border-box",
  },
};

const FRAMEWORK_CHIP_SX = (active) => ({
  height: 24,
  minWidth: "auto",
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: "6px",
  backgroundColor: active ? "#ecfdf5" : "#f1f5f9",
  color: active ? "#16a34a" : "#94a3b8",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: active ? "#d1fae5" : "#e2e8f0",
  },
  "& .MuiChip-label": {
    px: 1,
    whiteSpace: "nowrap",
  },
});

const SUPPRESSION_CHIP_SX = (active) => ({
  height: 24,
  minWidth: "auto",
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: "6px",
  backgroundColor: active ? "#eff6ff" : "#f1f5f9",
  color: active ? "#2563eb" : "#94a3b8",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: active ? "#dbeafe" : "#e2e8f0",
  },
  "& .MuiChip-label": {
    px: 1,
    whiteSpace: "nowrap",
  },
});

const MULTI_BTN_SX = {
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 600,
  minWidth: "auto",
  px: 1.5,
  py: 0.5,
  height: 28,
  borderRadius: "6px",
  color: "#334155",
  borderColor: "#e2e8f0",
  backgroundColor: "#f8fafc",
  "&:hover": {
    borderColor: "#cbd5e1",
    backgroundColor: "#f1f5f9",
  },
};

const Suppression = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { selectedProvider } = useContext(CloudProviderContext);
  const isAzureView = shouldShowAzurePlaceholderView(selectedProvider);
  const accountLabels = getSuppressionAccountLabels(isAzureView);

  const { schedules, isFetchingSchedules, fetchSchedulesError } = useSelector(
    (state) => state.suppression
  );
  const accounts = useSelector((state) => state.acctList?.accounts);

  // Filter state
  const [selectedAccountNumber, setSelectedAccountNumber] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Modal state for all schedule types
  const [tagBasedModalOpen, setTagBasedModalOpen] = useState(false);
  const [selectedAccountForTagBased, setSelectedAccountForTagBased] = useState(null);
  const [tagBasedSchedules, setTagBasedSchedules] = useState([]);
  
  const [recurringModalOpen, setRecurringModalOpen] = useState(false);
  const [selectedAccountForRecurring, setSelectedAccountForRecurring] = useState(null);
  const [recurringSchedules, setRecurringSchedules] = useState([]);
  
  const [onDemandModalOpen, setOnDemandModalOpen] = useState(false);
  const [selectedAccountForOnDemand, setSelectedAccountForOnDemand] = useState(null);
  const [onDemandSchedules, setOnDemandSchedules] = useState([]);
  
  const [wafrModalOpen, setWafrModalOpen] = useState(false);
  const [selectedAccountForWafr, setSelectedAccountForWafr] = useState(null);
  const [wafrSchedules, setWafrSchedules] = useState([]);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const API_PAGE_SIZE = 100; // API fetches 100 records at a time
  const DEFAULT_PAGE_SIZE = 25;
  
  // Get page size from localStorage or use default
  const getStoredPageSize = () => {
    const stored = localStorage.getItem("suppression_page_size");
    return stored ? parseInt(stored, 10) : DEFAULT_PAGE_SIZE;
  };
  
  const [pageSize, setPageSize] = useState(getStoredPageSize());
  
  // Store all schedules data and track fetched API pages
  const [allSchedules, setAllSchedules] = useState(null);
  const [fetchedApiPages, setFetchedApiPages] = useState(new Set([1])); // Track which API pages have been fetched

  // Fetch schedules on mount
  useEffect(() => {
    const params = {
      page: 1,
      page_size: API_PAGE_SIZE,
    };
    dispatch(getSchedules(params));
  }, [dispatch]);
  
  // Check if we need to fetch more data when page changes
  useEffect(() => {
    if (!allSchedules?.items) return;
    
    // Calculate which API page we need based on current UI page and page size
    // We need to account for filtered data, so we check if we have enough raw data
    const recordsNeeded = page * pageSize;
    const apiPageNeeded = Math.ceil(recordsNeeded / API_PAGE_SIZE);
    
    // If we haven't fetched this API page yet, fetch it
    if (apiPageNeeded > 0 && !fetchedApiPages.has(apiPageNeeded)) {
      const params = {
        page: apiPageNeeded,
        page_size: API_PAGE_SIZE,
      };
      
      dispatch(getSchedules(params));
      setFetchedApiPages((prev) => new Set([...prev, apiPageNeeded]));
    }
  }, [page, pageSize, allSchedules, fetchedApiPages, dispatch]);

  // Update allSchedules when schedules data is received (merge with existing data)
  useEffect(() => {
    if (schedules && !isFetchingSchedules && schedules.items) {
      setAllSchedules((prev) => {
        if (!prev) {
          return schedules;
        }
        // Merge items, avoiding duplicates
        const existingIds = new Set(prev.items.map((s) => s.schedule_id));
        const newItems = schedules.items.filter(
          (s) => !existingIds.has(s.schedule_id)
        );
        return {
          ...schedules,
          items: [...prev.items, ...newItems],
          total: Math.max(prev.total || 0, schedules.total || 0),
        };
      });
    }
  }, [schedules, isFetchingSchedules]);

  // Helper function to check if schedule is WAFR
  const isWAFRSchedule = (schedule) => {
    if (!schedule) return false;
    const scheduleName = schedule.schedule_name || "";
    return scheduleName.includes("WAFR") || scheduleName.includes("wafr") || 
           formatScheduleName(schedule) === "Well Architected Framework Review";
  };

  // Account numbers for the active cloud provider (AWS vs Azure toggle)
  const providerAccountNumbers = useMemo(() => {
    if (!accounts?.length) return new Set();
    return new Set(
      accounts
        .filter(
          (account) =>
            account?.accountNumber &&
            !String(account.accountNumber).includes("INIT") &&
            getAccountCloudType(account) === selectedProvider
        )
        .map((account) => account.accountNumber)
    );
  }, [accounts, selectedProvider]);

  // Drop account filter if it no longer belongs to the selected provider
  useEffect(() => {
    if (
      selectedAccountNumber &&
      !providerAccountNumbers.has(selectedAccountNumber)
    ) {
      setSelectedAccountNumber("");
      setPage(1);
    }
  }, [selectedProvider, providerAccountNumbers, selectedAccountNumber]);

  // Get unique account numbers from schedules for the selected provider only
  const availableAccountNumbers = useMemo(() => {
    if (!allSchedules?.items) return [];
    const accountSet = new Set();
    allSchedules.items.forEach((schedule) => {
      if (
        schedule.account_number &&
        providerAccountNumbers.has(schedule.account_number)
      ) {
        accountSet.add(schedule.account_number);
      }
    });
    return Array.from(accountSet).sort();
  }, [allSchedules, providerAccountNumbers]);

  // Group and categorize schedules by account
  const groupedSchedulesByAccount = useMemo(() => {
    if (!allSchedules?.items) return [];
    
    let filtered = allSchedules.items.filter((schedule) =>
      providerAccountNumbers.has(schedule.account_number)
    );

    // Filter by account number
    if (selectedAccountNumber) {
      filtered = filtered.filter(
        (schedule) => schedule.account_number === selectedAccountNumber
      );
    }


    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((schedule) => {
        const displayName = formatScheduleName(schedule).toLowerCase();
        const accountNumber = (schedule.account_number || "").toLowerCase();
        return displayName.includes(searchLower) || accountNumber.includes(searchLower);
      });
    }

    // Group by account_number
    const accountMap = new Map();
    
    filtered.forEach((schedule) => {
      const accountNum = schedule.account_number;
      if (!accountNum) return;
      
      if (!accountMap.has(accountNum)) {
        accountMap.set(accountNum, {
          account_number: accountNum,
          recurring: [],
          onDemand: [],
          wafr: [],
          tagBased: [],
        });
      }
      
      const accountData = accountMap.get(accountNum);
      
      // Categorize schedule
      if (schedule.resource_scope_type === "tag_based") {
        accountData.tagBased.push(schedule);
      } else if (schedule.resource_scope_type === "full") {
        if (schedule.schedule_type === "recurring") {
          accountData.recurring.push(schedule);
        } else if (schedule.schedule_type === "on_demand") {
          if (isWAFRSchedule(schedule)) {
            accountData.wafr.push(schedule);
          } else {
            accountData.onDemand.push(schedule);
          }
        }
      }
    });
    
    // Convert to array and sort by account number
    const result = Array.from(accountMap.values()).sort((a, b) => 
      (a.account_number || "").localeCompare(b.account_number || "")
    );
    
    // Apply pagination
    const total = result.length;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = result.slice(startIndex, endIndex);
    
    return {
      items: paginatedItems,
      total,
      page,
      page_size: pageSize,
    };
  }, [
    allSchedules,
    selectedAccountNumber,
    searchTerm,
    page,
    pageSize,
    providerAccountNumbers,
  ]);

  // Clear state on unmount
  useEffect(() => {
    return () => {
      dispatch(clearSuppressionState());
    };
  }, [dispatch]);

  // Handle filter changes
  const handleAccountChange = (accountNumber) => {
    setSelectedAccountNumber(accountNumber);
    setPage(1); // Reset to first page when filter changes
  };

  
  // Handle page size change
  const handlePageSizeChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    localStorage.setItem("suppression_page_size", newPageSize.toString());
    setPage(1); // Reset to first page when page size changes
  };

  // Handle pagination
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  // Handle view button click - navigate to frameworks page
  const handleViewSchedule = (schedule) => {
    console.log("View schedule clicked:", schedule);
    // Store schedule data in sessionStorage to carry forward
    sessionStorage.setItem("selectedSchedule", JSON.stringify(schedule));
    const path = `/configuration/suppression/frameworks/${schedule.schedule_id}`;
    console.log("Navigating to:", path);
    history.push(path);
  };

  // Handle view suppressions button click
  const handleViewSuppressions = (schedule) => {
    // Store schedule data in sessionStorage
    sessionStorage.setItem("selectedSchedule", JSON.stringify(schedule));
    history.push(`/configuration/suppression/schedules/${schedule.schedule_id}/suppressions`);
  };

  // Handle tag based click
  const handleTagBasedClick = (accountNumber, tagBasedSchedules) => {
    if (tagBasedSchedules.length === 1) {
      // If only one, directly use it - icons will handle it
      return;
    } else {
      // Open modal with list
      setSelectedAccountForTagBased(accountNumber);
      setTagBasedSchedules(tagBasedSchedules);
      setTagBasedModalOpen(true);
    }
  };

  // Handle recurring click
  const handleRecurringClick = (accountNumber, schedules) => {
    if (schedules.length === 1) {
      return;
    } else {
      setSelectedAccountForRecurring(accountNumber);
      setRecurringSchedules(schedules);
      setRecurringModalOpen(true);
    }
  };

  // Handle on demand click
  const handleOnDemandClick = (accountNumber, schedules) => {
    if (schedules.length === 1) {
      return;
    } else {
      setSelectedAccountForOnDemand(accountNumber);
      setOnDemandSchedules(schedules);
      setOnDemandModalOpen(true);
    }
  };

  // Handle WAFR click
  const handleWafrClick = (accountNumber, schedules) => {
    if (schedules.length === 1) {
      return;
    } else {
      setSelectedAccountForWafr(accountNumber);
      setWafrSchedules(schedules);
      setWafrModalOpen(true);
    }
  };

  // Helper function to format large numbers (only format very large numbers to preserve default appearance)
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 10000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    // For numbers < 10000, show full number to maintain good default appearance
    return num.toString();
  };

  // Shared schedule list dialog (Tag Based / Recurring / On Demand / WAFR)
  const renderScheduleModal = (open, onClose, title, schedules, accountNumber) => {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "calc(100vh - 48px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "none",
            border: "1px solid #e2e8f0",
            ...ACCOUNT_DETAIL_FONT_SX,
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: "#F98125",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.125rem", sm: "1.25rem" },
                color: "#FFFFFF",
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                mt: 0.75,
                color: "rgba(255,255,255,0.9)",
                fontSize: "0.875rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {accountLabels.numberHeader}:{" "}
              <Box component="span" sx={{ fontWeight: 600, color: "#FFFFFF" }}>
                {accountNumber || "N/A"}
              </Box>
            </Typography>
          </Box>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              padding: 0,
              margin: 0,
              border: "none",
              background: "transparent",
              color: "#FFFFFF",
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              opacity: 1,
              boxShadow: "none",
              flexShrink: 0,
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </Box>

        <DialogContent
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            backgroundColor: "#f3f4f6",
            flex: 1,
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              backgroundColor: "#fff",
              overflow: "hidden",
            }}
          >
            <TableContainer
              sx={{
                width: "100%",
                maxWidth: "100%",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <Table
                size="small"
                sx={{ width: "100%", minWidth: 420, tableLayout: "fixed" }}
              >
                <colgroup>
                  <col style={{ width: "62%" }} />
                  <col style={{ width: "38%" }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...HEADER_CELL_SX, pl: 2 }}>
                      Schedule Name
                    </TableCell>
                    <TableCell align="center" sx={HEADER_CELL_SX}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedules.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        align="center"
                        sx={{ ...BODY_CELL_SX, py: 5 }}
                      >
                        <Typography sx={{ ...CELL_TEXT, color: "#64748b" }}>
                          No schedules found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    schedules.map((schedule, index) => {
                      const frameworkCount = schedule.framework_count ?? 0;
                      const suppressionCount = schedule.suppression_count ?? 0;
                      const formattedFrameworkCount =
                        formatNumber(frameworkCount);
                      const formattedSuppressionCount =
                        formatNumber(suppressionCount);
                      return (
                        <TableRow
                          key={schedule.schedule_id || index}
                          sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}
                        >
                          <TableCell sx={{ ...BODY_CELL_SX, pl: 2 }}>
                            <TruncatedTooltipText sx={CELL_TEXT}>
                              {formatScheduleName(schedule)}
                            </TruncatedTooltipText>
                          </TableCell>
                          <TableCell align="center" sx={BODY_CELL_SX}>
                            <Box
                              sx={{
                                display: "inline-flex",
                                gap: 1,
                                alignItems: "center",
                                justifyContent: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              <AccountDetailTooltip
                                title={`Frameworks: ${frameworkCount.toLocaleString()}`}
                              >
                                <Chip
                                  label={
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <span>{formattedFrameworkCount}</span>
                                      <EditIcon
                                        sx={{
                                          fontSize: 14,
                                          color:
                                            frameworkCount > 0
                                              ? "#16a34a"
                                              : "#94a3b8",
                                        }}
                                      />
                                    </Box>
                                  }
                                  size="small"
                                  onClick={() => {
                                    handleViewSchedule(schedule);
                                    onClose();
                                  }}
                                  sx={FRAMEWORK_CHIP_SX(frameworkCount > 0)}
                                />
                              </AccountDetailTooltip>
                              <AccountDetailTooltip
                                title={`View Suppressions: ${suppressionCount.toLocaleString()}`}
                              >
                                <Chip
                                  label={
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <span>{formattedSuppressionCount}</span>
                                      <VisibilityIcon
                                        sx={{
                                          fontSize: 14,
                                          color:
                                            suppressionCount > 0
                                              ? "#2563eb"
                                              : "#94a3b8",
                                        }}
                                      />
                                    </Box>
                                  }
                                  size="small"
                                  onClick={() => {
                                    handleViewSuppressions(schedule);
                                    onClose();
                                  }}
                                  sx={SUPPRESSION_CHIP_SX(suppressionCount > 0)}
                                />
                              </AccountDetailTooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
            }}
          >
            <Button
              onClick={onClose}
              sx={{
                height: 36,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                borderRadius: "8px",
                px: 2,
                color: "#334155",
                backgroundColor: "#fff",
                border: "1px solid #e2e8f0",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#f8fafc",
                  borderColor: "#cbd5e1",
                  boxShadow: "none",
                },
              }}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  // Helper function to safely extract error message
  const getErrorMessage = (error) => {
    if (!error) return "An unexpected error occurred";
    if (typeof error === "string") return error;
    if (typeof error === "object") {
      if (error.error && typeof error.error === "string") return error.error;
      if (error.message && typeof error.message === "string")
        return error.message;
      if (error.error && typeof error.error === "object") {
        if (error.error.message) return error.error.message;
        if (error.error.error) return error.error.error;
      }
      try {
        const stringified = JSON.stringify(error);
        if (stringified.length > 200) {
          return "An error occurred. Please check the console for details.";
        }
        return stringified;
      } catch {
        return "An unexpected error occurred";
      }
    }
    return String(error);
  };

  // Get pagination info
  const totalPages = groupedSchedulesByAccount?.total
    ? Math.ceil(groupedSchedulesByAccount.total / pageSize)
    : 0;
  const currentPageStart = groupedSchedulesByAccount?.items?.length
    ? (page - 1) * pageSize + 1
    : 0;
  const currentPageEnd = groupedSchedulesByAccount?.items?.length
    ? Math.min(page * pageSize, groupedSchedulesByAccount.total || 0)
    : 0;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        p: { xs: 1.5, sm: 2.5 },
        backgroundColor: "#f3f4f6",
        boxSizing: "border-box",
        ...ACCOUNT_DETAIL_FONT_SX,
      }}
    >
      <Box
        sx={{
          pb: 1.25,
          mb: 1.75,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            color: "#111827",
            fontSize: "1rem",
          }}
        >
          Compliance Configuration
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography
          sx={{
            color: "#64748b",
            fontSize: "0.875rem",
            fontWeight: 400,
            lineHeight: 1.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          Manage compliance control and check suppressions by account and schedule.
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            width: { xs: "100%", sm: "auto" },
            flexShrink: 0,
          }}
        >
          <TextField
            size="small"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={SEARCH_FIELD_SX}
          />
          <SuppressionFilters
            availableAccountNumbers={availableAccountNumbers}
            selectedAccountNumber={selectedAccountNumber}
            onAccountChange={handleAccountChange}
            isAzureView={isAzureView}
          />
        </Box>
      </Box>

      {isFetchingSchedules && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            backgroundColor: "#fff",
          }}
        >
          <CircularProgress size={32} sx={{ color: "#64748b" }} />
          <Typography
            sx={{
              ml: 2,
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Loading schedules...
          </Typography>
        </Box>
      )}

      {fetchSchedulesError && !isFetchingSchedules && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: "#fef2f2",
            borderRadius: "8px",
            border: "1px solid #fecaca",
          }}
        >
          <Typography
            sx={{
              color: "#991b1b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            {getErrorMessage(fetchSchedulesError)}
          </Typography>
        </Box>
      )}

      {allSchedules && !isFetchingSchedules && !fetchSchedulesError && (
        <Box
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            backgroundColor: "#fff",
            overflow: "hidden",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
            <TableContainer
              sx={{
                width: "100%",
                maxWidth: "100%",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <Table
                size="small"
                sx={{
                  width: "100%",
                  minWidth: 920,
                  tableLayout: "fixed",
                }}
              >
                <colgroup>
                  <col style={{ width: "28%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "18%" }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell align="left" sx={{ ...HEADER_CELL_SX, pl: 2 }}>
                      {accountLabels.numberHeader}
                    </TableCell>
                    <TableCell align="center" sx={HEADER_CELL_SX}>
                      Recurring
                    </TableCell>
                    <TableCell align="center" sx={HEADER_CELL_SX}>
                      On Demand
                    </TableCell>
                    <TableCell align="center" sx={HEADER_CELL_SX}>
                      <AccountDetailTooltip title="Well Architected Framework Review">
                        <span>WAFR</span>
                      </AccountDetailTooltip>
                    </TableCell>
                    <TableCell align="center" sx={HEADER_CELL_SX}>
                      Tag Based
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedSchedulesByAccount?.items && groupedSchedulesByAccount.items.length > 0 ? (
                    groupedSchedulesByAccount.items.map((accountData, index) => {
                      const formatNumber = (num) => {
                        if (num >= 1000000) {
                          return `${(num / 1000000).toFixed(1)}M`;
                        } else if (num >= 10000) {
                          return `${(num / 1000).toFixed(1)}K`;
                        }
                        return num.toString();
                      };

                      const renderActionIcons = (schedule) => {
                        if (!schedule) return null;
                        const suppressionCount = schedule.suppression_count ?? 0;
                        const frameworkCount = schedule.framework_count ?? 0;
                        const formattedFrameworkCount = formatNumber(frameworkCount);
                        const formattedSuppressionCount = formatNumber(suppressionCount);
                        return (
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "center" }}>
                            <AccountDetailTooltip title={`Frameworks: ${frameworkCount.toLocaleString()}`}>
                              <Chip
                                label={
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <span>{formattedFrameworkCount}</span>
                                    <EditIcon sx={{ fontSize: 14, color: frameworkCount > 0 ? "#16a34a" : "#94a3b8" }} />
                                  </Box>
                                }
                                size="small"
                                onClick={() => handleViewSchedule(schedule)}
                                sx={FRAMEWORK_CHIP_SX(frameworkCount > 0)}
                              />
                            </AccountDetailTooltip>
                            <AccountDetailTooltip title={`View Suppressions: ${suppressionCount.toLocaleString()}`}>
                              <Chip
                                label={
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <span>{formattedSuppressionCount}</span>
                                    <VisibilityIcon sx={{ fontSize: 14, color: suppressionCount > 0 ? "#2563eb" : "#94a3b8" }} />
                                  </Box>
                                }
                                size="small"
                                onClick={() => handleViewSuppressions(schedule)}
                                sx={SUPPRESSION_CHIP_SX(suppressionCount > 0)}
                              />
                            </AccountDetailTooltip>
                          </Box>
                        );
                      };

                      const renderScheduleCell = (schedules, onClickHandler, typeLabel = "workloads") => {
                        if (!schedules || schedules.length === 0) {
                          return <Typography sx={{ ...CELL_TEXT, color: "#94a3b8" }}>—</Typography>;
                        }
                        if (schedules.length === 1) {
                          return renderActionIcons(schedules[0]);
                        }
                        return (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => onClickHandler(accountData.account_number, schedules)}
                            sx={MULTI_BTN_SX}
                          >
                            {schedules.length} {typeLabel}
                          </Button>
                        );
                      };

                      const renderTagBasedCell = () => {
                        const tagBased = accountData.tagBased || [];
                        return renderScheduleCell(tagBased, handleTagBasedClick, "workloads");
                      };

                      return (
                        <TableRow
                          key={accountData.account_number || index}
                          sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}
                        >
                          <TableCell align="left" sx={{ ...BODY_CELL_SX, pl: 2 }}>
                            <Typography
                              sx={{
                                ...CELL_TEXT,
                                fontWeight: 600,
                                color: "#0f172a",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={accountData.account_number || "N/A"}
                            >
                              {accountData.account_number || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={BODY_CELL_SX}>
                            {renderScheduleCell(accountData.recurring || [], handleRecurringClick, "schedules")}
                          </TableCell>
                          <TableCell align="center" sx={BODY_CELL_SX}>
                            {renderScheduleCell(accountData.onDemand || [], handleOnDemandClick, "schedules")}
                          </TableCell>
                          <TableCell align="center" sx={BODY_CELL_SX}>
                            {renderScheduleCell(accountData.wafr || [], handleWafrClick, "schedules")}
                          </TableCell>
                          <TableCell align="center" sx={BODY_CELL_SX}>
                            {renderTagBasedCell()}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        sx={{ ...BODY_CELL_SX, py: 6 }}
                      >
                        <Typography sx={{ ...CELL_TEXT, color: "#64748b" }}>
                          No schedules found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
                px: 2,
                py: 1,
                borderTop: "1px solid #e2e8f0",
                backgroundColor: "#f8fafc",
                minHeight: 48,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#16a34a",
                      flexShrink: 0,
                    }}
                  />
                  <Typography sx={{ color: "#64748b", fontSize: "0.75rem" }}>
                    Frameworks
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#2563eb",
                      flexShrink: 0,
                    }}
                  />
                  <Typography sx={{ color: "#64748b", fontSize: "0.75rem" }}>
                    Suppressions
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", ml: "auto" }}>
                <Typography sx={{ color: "#64748b", fontSize: "0.75rem" }}>
                  Rows per page
                </Typography>
                <FormControl size="small">
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    sx={{
                      fontSize: "0.75rem",
                      height: 28,
                      backgroundColor: "#fff",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e2e8f0",
                      },
                    }}
                  >
                    <MenuItem value={10} sx={{ fontSize: "0.8rem" }}>
                      10
                    </MenuItem>
                    <MenuItem value={25} sx={{ fontSize: "0.8rem" }}>
                      25
                    </MenuItem>
                    <MenuItem value={50} sx={{ fontSize: "0.8rem" }}>
                      50
                    </MenuItem>
                    <MenuItem value={100} sx={{ fontSize: "0.8rem" }}>
                      100
                    </MenuItem>
                  </Select>
                </FormControl>
                <Typography sx={{ color: "#64748b", fontSize: "0.75rem", ml: 0.5 }}>
                  {groupedSchedulesByAccount?.total
                    ? `${currentPageStart}–${currentPageEnd} of ${groupedSchedulesByAccount.total}`
                    : "0 of 0"}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  sx={{ color: page === 1 ? "#d1d5db" : "#64748b" }}
                >
                  <FirstPageIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  sx={{ color: page === 1 ? "#d1d5db" : "#64748b" }}
                >
                  <ArrowBackIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages || totalPages === 0}
                  sx={{
                    color:
                      page >= totalPages || totalPages === 0
                        ? "#d1d5db"
                        : "#64748b",
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages || totalPages === 0}
                  sx={{
                    color:
                      page >= totalPages || totalPages === 0
                        ? "#d1d5db"
                        : "#64748b",
                  }}
                >
                  <LastPageIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
        </Box>
      )}

      {/* Tag Based Modal */}
      {renderScheduleModal(
        tagBasedModalOpen,
        () => {
          setTagBasedModalOpen(false);
          setSelectedAccountForTagBased(null);
          setTagBasedSchedules([]);
        },
        "Tag Based Workloads",
        tagBasedSchedules,
        selectedAccountForTagBased
      )}

      {/* Recurring Modal */}
      {renderScheduleModal(
        recurringModalOpen,
        () => {
          setRecurringModalOpen(false);
          setSelectedAccountForRecurring(null);
          setRecurringSchedules([]);
        },
        "Recurring Schedules",
        recurringSchedules,
        selectedAccountForRecurring
      )}

      {/* On Demand Modal */}
      {renderScheduleModal(
        onDemandModalOpen,
        () => {
          setOnDemandModalOpen(false);
          setSelectedAccountForOnDemand(null);
          setOnDemandSchedules([]);
        },
        "On Demand Schedules",
        onDemandSchedules,
        selectedAccountForOnDemand
      )}

      {/* WAFR Modal */}
      {renderScheduleModal(
        wafrModalOpen,
        () => {
          setWafrModalOpen(false);
          setSelectedAccountForWafr(null);
          setWafrSchedules([]);
        },
        "WAFR Schedules",
        wafrSchedules,
        selectedAccountForWafr
      )}
    </Box>
  );
};

export default Suppression;

